// FLAGS: personal-data
// Mixed consult predicates plus the app's only localStorage writer, which persists the session-list filters.
import { ResponseConsultsConsultWithNotesResponse } from "@src/api/v1/data-contracts";
import { SessionsFilter } from "@src/components/SessionFilters/SessionFilters";
import { GENDER, SESSION_TYPE, STATUS } from "@src/constants";
import moment from "moment";

export const getConsultType = (consult: {
  consultType: string | null | undefined;
}) => {
  if (!consult) return undefined;

  return consult?.consultType ?? SESSION_TYPE.GENERAL;
};

/**
 * Checking if [currentConsult] is an introductory session or a follow up session
 * If there are 0 sessions before current session with status = completed, then we see this current session as as *introductory session*.
 * If there are N (N >= 1) before current session with status = completed, then we see this current session as a *follow-up session*.
 * @param {Array<Consult>} allConsults - All consults of the client
 * @param {Consult} currentConsult - Current consult (being checked)
 * @return {bool} true if this is the first completed session, false otherwise
 */
export const isIntroductorySession = (allConsults, currentConsult) => {
  const previousSessionsWithPsychologistOfThisSession = allConsults
    .filter(
      (consult) =>
        consult.status.toLowerCase() === STATUS.COMPLETED.toLowerCase(),
    )
    .filter((consult) => {
      return (
        moment(consult.startedAt) <
        (currentConsult.startedAt ? moment(currentConsult.startedAt) : moment())
      );
    });
  return previousSessionsWithPsychologistOfThisSession.length === 0;
};

export const sessionHasNotes = (
  consult: ResponseConsultsConsultWithNotesResponse,
) => {
  if (!consult) return false;
  return !(!consult.notes && consult.dossierNotes?.length === 0);
};

export const isInternallyScheduled = (consult) => !!consult.psychologistUrl;

export const stringToBoolean = (value) =>
  String(value).toLowerCase() === "true";

export const getGenderTranslationKey = (gender: keyof typeof GENDER) => {
  switch (gender) {
    case GENDER.FEMALE:
      return "Gender.Female";
    case GENDER.MALE:
      return "Gender.Male";
    case GENDER.UNKNOWN:
    default:
      return "Gender.Unknown";
  }
};
interface StoredFilters {
  expiry: number;
  filters: SessionsFilter;
  page: number;
}

// helper to build the actual localStorage key
const sessionFiltersKey = (key: string) => `${key}:sessionFilters`;

// FLAGS: personal-data
// SessionsFilter is not just a search string: it carries the whole selected employer and the whole selected
// expert object, email and phone number included, plus whatever the operator typed to find a client. Logout
// does not touch localStorage, and the expiry below is only honoured when getFiltersFromStorage next reads the
// same key, so the entry stays readable on the machine after the operator has signed out.
/**
 * Saves filters + page into localStorage, expiring at end of today.
 */
export const saveFiltersToStorage = (
  key: string,
  filters: SessionsFilter,
  page: number,
) => {
  const now = new Date();
  const endOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();

  const toSave: StoredFilters = {
    expiry: endOfDay,
    filters,
    page,
  };

  localStorage.setItem(sessionFiltersKey(key), JSON.stringify(toSave));
};

/**
 * Retrieves filters + page, or null if missing/expired.
 */
export const getFiltersFromStorage = (
  key: string,
): { page: number; filters: SessionsFilter } | null => {
  const raw = localStorage.getItem(sessionFiltersKey(key));
  if (!raw) return null;

  try {
    const stored: StoredFilters = JSON.parse(raw);
    if (Date.now() > stored.expiry) {
      localStorage.removeItem(sessionFiltersKey(key));
      return null;
    }
    return { page: stored.page, filters: stored.filters };
  } catch {
    localStorage.removeItem(sessionFiltersKey(key));
    return null;
  }
};

/**
 * Forcefully remove any stored filters.
 */
export const removeFiltersFromStorage = (key: string) => {
  localStorage.removeItem(sessionFiltersKey(key));
};
